import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BomBySubsystem from "@/components/BomBySubsystem";
import LegendPanel from "@/components/LegendPanel";
import KmzPanel from "@/components/KmzPanel";
import PlanKmzOverlay from "@/components/PlanKmzOverlay";
import {
  UploadCloud, Loader2, FileText, Download, Share2, Sparkles,
  ChevronLeft, AlertTriangle, CheckCircle2, Info, Save,
  ClipboardList, MapPin, Ruler, FileSearch, PencilLine, GitCompare, Video, Wand2,
  MessageCircle
} from "lucide-react";
import api, { API_BASE } from "@/lib/api";

const CLASS_COLORS = {
  outlet: "#EA580C",
  switch: "#F59E0B",
  light_fixture: "#00C853",
  data_point: "#003366",
  access_point: "#003366",
  cctv_camera: "#EA580C",
  rack: "#0A0A0A",
  cabinet: "#52525B",
  junction_box: "#EA580C",
  conduit: "#F59E0B",
  cable_tray: "#00C853",
  mdf: "#003366",
  idf: "#002244",
};

const fmtUsd = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

export default function ProjectDetail() {
  const { t, i18n } = useTranslation();
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [estimation, setEstimation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState("viewer");
  const [activePage, setActivePage] = useState(0);
  const [classFilter, setClassFilter] = useState("all");
  const [aiRunning, setAiRunning] = useState(false);
  const [bomLocal, setBomLocal] = useState([]);
  const [bomDirty, setBomDirty] = useState(false);
  const [savingBom, setSavingBom] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [toast, setToast] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [surveyUploading, setSurveyUploading] = useState(false);
  const [module, setModule] = useState(() => localStorage.getItem("iqes_module") || "low_voltage");
  const fileRef = useRef(null);
  const surveyRef = useRef(null);
  const videoRef = useRef(null);
  const dwgRef = useRef(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [dwgUploading, setDwgUploading] = useState(false);
  const [autodeskReady, setAutodeskReady] = useState(false);

  // Persist module choice
  useEffect(() => { localStorage.setItem("iqes_module", module); }, [module]);

  // Detect whether Autodesk is configured so we can show the DWG button
  useEffect(() => {
    api.get("/autodesk/status")
      .then(r => setAutodeskReady(!!r.data?.configured))
      .catch(() => setAutodeskReady(false));
  }, []);

  const flash = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2200);
  };

  const loadProject = async () => {
    setLoading(true);
    try {
      const { data: p } = await api.get(`/projects/${projectId}`);
      setProject(p);
      if (p.last_estimation_id) {
        const { data: est } = await api.get(`/estimations/${p.last_estimation_id}`);
        setEstimation(est);
        setBomLocal(est.bom);
      } else {
        setEstimation(null);
        setBomLocal([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProject(); }, [projectId]);

  const loadSurveys = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/site-surveys`);
      setSurveys(data || []);
    } catch {}
  };

  useEffect(() => { loadSurveys(); }, [projectId]);

  const onSurveyUpload = async (file) => {
    if (!file) return;
    setSurveyUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("language", i18n.language?.startsWith("es") ? "es" : "en");
    try {
      await api.post(`/projects/${projectId}/site-survey`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 240000,
      });
      flash("Site survey processed ✓");
      loadSurveys();
      setTab("survey");
    } catch (e) {
      flash(e?.response?.data?.detail || "Site survey failed", "error");
    } finally {
      setSurveyUploading(false);
      if (surveyRef.current) surveyRef.current.value = "";
    }
  };

  const onVideoUpload = async (file) => {
    if (!file || !estimation?.id) return;
    setVideoUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(
        `/estimations/${estimation.id}/site-surveys/video`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 }
      );
      flash(`Video processed ✓ ${data.frames_analyzed} frames · ${data.devices_found} devices`);
      loadSurveys();
      setTab("survey");
    } catch (e) {
      flash(e?.response?.data?.detail || "Video analysis failed", "error");
    } finally {
      setVideoUploading(false);
      if (videoRef.current) videoRef.current.value = "";
    }
  };

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/projects/${projectId}/estimate`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEstimation(data);
      setBomLocal(data.bom);
      setActivePage(0);
      setTab("viewer");
      flash("Blueprint processed ✓");
      loadProject();
    } catch (e) {
      flash(e?.response?.data?.detail || "Upload failed", "error");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDwgUpload = async (file) => {
    if (!file) return;
    setDwgUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      flash(i18n.language?.startsWith("es")
        ? "Subiendo a Autodesk y convirtiendo... (puede tardar 1-2 min)"
        : "Uploading to Autodesk and converting... (may take 1-2 min)");
      const { data } = await api.post(`/projects/${projectId}/upload-dwg`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 5 * 60 * 1000, // 5 min
      });
      setEstimation(data);
      setBomLocal(data.bom);
      setActivePage(0);
      setTab("viewer");
      flash(i18n.language?.startsWith("es")
        ? "DWG/RVT convertido ✓"
        : "DWG/RVT converted ✓");
      loadProject();
    } catch (e) {
      flash(e?.response?.data?.detail || "Autodesk conversion failed", "error");
    } finally {
      setDwgUploading(false);
      if (dwgRef.current) dwgRef.current.value = "";
    }
  };

  const runAi = async () => {
    if (!estimation) return;
    setAiRunning(true);
    try {
      const lang = i18n.language?.startsWith("es") ? "es" : "en";
      const { data } = await api.post(`/estimations/${estimation.id}/analyze?language=${lang}`);
      setEstimation({ ...estimation, ai_analysis: data });
      setTab("ai");
      flash("AI analysis ready ✓");
    } catch (e) {
      flash(e?.response?.data?.detail || "AI analysis failed", "error");
    } finally {
      setAiRunning(false);
    }
  };

  const updateBomRow = (idx, field, value) => {
    const copy = [...bomLocal];
    const row = { ...copy[idx], [field]: field === "quantity" || field === "unit_cost" ? parseFloat(value) || 0 : value };
    row.total_cost = Math.round(row.quantity * row.unit_cost * 100) / 100;
    copy[idx] = row;
    setBomLocal(copy);
    setBomDirty(true);
  };

  const saveBom = async () => {
    setSavingBom(true);
    try {
      const { data } = await api.put(`/estimations/${estimation.id}/bom`, bomLocal);
      setEstimation({ ...estimation, bom: bomLocal, totals: data.totals });
      setBomDirty(false);
      flash(t("bom.saved"));
    } finally {
      setSavingBom(false);
    }
  };

  const download = async (fmt) => {
    try {
      const token = localStorage.getItem("iqes_token");
      const url = `${API_BASE}/estimations/${estimation.id}/export/${fmt}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `estimation-${estimation.id}.${fmt}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      flash("Export failed", "error");
    }
  };

  const pushToCrm = async () => {
    if (!estimation) return;
    setPushing(true);
    try {
      await api.post(`/integrations/crm/push/${estimation.id}`);
      flash("Queued for CRM ✓");
    } catch (e) {
      flash(e?.response?.data?.detail || "CRM push failed", "error");
    } finally {
      setPushing(false);
    }
  };

  const pushResultsToCrm = async () => {
    if (!project?.id) return;
    setPushing(true);
    try {
      const r = await api.post(`/integrations/crm/push-results/${project.id}`);
      const q = r.data?.quote;
      const w = r.data?.work_order;
      const parts = [];
      if (q?.ok) parts.push(`Quote ${q.crm_quote_id?.slice(0,8) || "OK"} (${q.items} items)`);
      else if (q?.error) parts.push(`Quote ✕ ${q.error}`);
      if (w?.ok) parts.push(`OT ${w.crm_work_order_id?.slice(0,8) || "OK"} (${w.phases} phases)`);
      else if (w?.error) parts.push(`OT ✕ ${w.error}`);
      flash(parts.join(" · ") || "Enviado al CRM ✓");
    } catch (e) {
      flash(e?.response?.data?.detail || "CRM results push failed", "error");
    } finally {
      setPushing(false);
    }
  };

  if (loading || !project) {
    return (
      <div className="p-6 text-slate-500 font-mono text-sm" data-testid="project-loading">Loading…</div>
    );
  }

  const pages = estimation?.pages || [];
  const currentPage = pages[activePage];
  // Module-aware filter: electrical | low_voltage | all
  const moduleFilter = (item) => {
    if (module === "all") return true;
    const cat = (item.category || "").toLowerCase();
    return cat === module || (module === "low_voltage" && cat === "lv");
  };
  const moduleDetections = (estimation?.detections || []).filter(moduleFilter);
  const moduleBom = (bomLocal || []).filter(moduleFilter);
  const moduleBomMaterials = Math.round(moduleBom.reduce((s, b) => s + (b.total_cost || 0), 0) * 100) / 100;

  const pageDetections = moduleDetections.filter((d) => d.page_index === activePage);
  const filteredDetections = classFilter === "all" ? pageDetections : pageDetections.filter((d) => d.class === classFilter);
  const classCounts = {};
  moduleDetections.forEach((d) => { classCounts[d.class] = (classCounts[d.class] || 0) + 1; });

  // Schedule per module (electrical keywords vs LV keywords)
  const moduleSchedule = (estimation?.schedule || []).filter((ph) => {
    if (module === "all") return true;
    const name = (ph.phase || "").toLowerCase();
    if (module === "electrical") return /electric|rough-in/i.test(ph.phase || "") && !/low voltage|cabling/i.test(name);
    if (module === "low_voltage") return /low voltage|cabling|testing|commissioning/i.test(ph.phase || "");
    return true;
  });

  // Compliance: NEC for electrical, TIA/BICSI for LV
  const moduleCompliance = (estimation?.compliance_notes || []).filter((c) => {
    if (module === "all") return true;
    const code = (c.code || "").toUpperCase();
    if (module === "electrical") return code.includes("NEC");
    if (module === "low_voltage") return code.includes("TIA") || code.includes("BICSI");
    return true;
  });

  return (
    <div className="p-6" data-testid="project-detail-page">
      {toast && (
        <div
          data-testid="toast"
          className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-xl text-sm font-mono shadow-lg border ${
            toast.type === "error" ? "bg-red-50 text-red-800 border-red-200" : "bg-green-50 text-green-800 border-green-200"
          }`}
        >
          {toast.msg}
        </div>
      )}

      <header className="mb-5">
        <Link to="/projects" data-testid="back-to-projects" className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-slate-500 hover:text-[#003366] transition-colors mb-2">
          <ChevronLeft size={14} /> {t("proj.title")}
        </Link>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">
              {project.client || "—"} · {project.location}
            </div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mt-1" data-testid="project-name">{project.name}</h1>
            {project.description && <p className="text-sm text-slate-600 mt-1 max-w-2xl">{project.description}</p>}
          </div>
          <ModuleSwitcher module={module} onChange={setModule} />
        </div>
        <div className="flex items-center gap-2 flex-wrap mt-4">
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              className="hidden"
              data-testid="upload-input"
              onChange={(e) => onUpload(e.target.files?.[0])}
            />
            <input
              ref={surveyRef}
              type="file"
              accept=".pdf,.docx"
              className="hidden"
              data-testid="survey-upload-input"
              onChange={(e) => onSurveyUpload(e.target.files?.[0])}
            />
            <input
              ref={videoRef}
              type="file"
              accept="video/mp4,video/quicktime,video/x-msvideo,.mp4,.mov,.avi"
              className="hidden"
              data-testid="video-upload-input"
              onChange={(e) => onVideoUpload(e.target.files?.[0])}
            />
            <input
              ref={dwgRef}
              type="file"
              accept=".dwg,.dxf,.rvt,.rfa,.ifc,.nwd,.nwc"
              className="hidden"
              data-testid="dwg-upload-input"
              onChange={(e) => onDwgUpload(e.target.files?.[0])}
            />
            <button
              data-testid="upload-survey-btn"
              onClick={() => surveyRef.current?.click()}
              disabled={surveyUploading}
              className="bg-white text-slate-900 border border-slate-300 hover:bg-slate-50 rounded-xl px-4 py-2 font-medium text-sm transition-colors inline-flex items-center gap-2 disabled:opacity-60"
            >
              {surveyUploading ? <Loader2 size={14} className="animate-spin" /> : <ClipboardList size={14} />}
              Site Survey
            </button>
            {autodeskReady && (
              <button
                data-testid="upload-dwg-btn"
                onClick={() => dwgRef.current?.click()}
                disabled={dwgUploading}
                className="bg-white text-[#0696D7] border border-[#0696D7]/40 hover:bg-[#0696D7]/5 rounded-xl px-4 py-2 font-medium text-sm transition-colors inline-flex items-center gap-2 disabled:opacity-60"
                title={i18n.language?.startsWith("es") ? "Sube DWG / DXF / RVT / IFC vía Autodesk" : "Upload DWG / DXF / RVT / IFC via Autodesk"}
              >
                {dwgUploading ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
                DWG / RVT
              </button>
            )}
            {estimation && (
              <button
                data-testid="upload-video-btn"
                onClick={() => videoRef.current?.click()}
                disabled={videoUploading}
                className="bg-white text-[#EA580C] border border-[#EA580C]/30 hover:bg-[#EA580C]/5 rounded-xl px-4 py-2 font-medium text-sm transition-colors inline-flex items-center gap-2 disabled:opacity-60"
              >
                {videoUploading ? <Loader2 size={14} className="animate-spin" /> : <Video size={14} />}
                {i18n.language?.startsWith("es") ? "Video Survey" : "Video Survey"}
              </button>
            )}
            {estimation && (
              <Link
                to={`/annotator/${estimation.id}`}
                data-testid="open-annotator-btn"
                className="bg-white text-[#003366] border border-[#003366] hover:bg-[#003366]/5 rounded-xl px-4 py-2 font-medium text-sm transition-colors inline-flex items-center gap-2"
              >
                <PencilLine size={14} />
                {t("pd.annotate") || "Anotador"}
              </Link>
            )}
            {estimation && (
              <Link
                to={`/projects/${projectId}/compare`}
                data-testid="open-compare-btn"
                className="bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 rounded-xl px-4 py-2 font-medium text-sm transition-colors inline-flex items-center gap-2"
              >
                <GitCompare size={14} />
                {t("pd.compare") || (t("common.compare") || "Compare")}
              </Link>
            )}
            <Link
              to={`/projects/${projectId}/analyzer`}
              data-testid="open-analyzer-btn"
              className="bg-[#003366] text-white hover:bg-[#002244] rounded-xl px-4 py-2 font-medium text-sm transition-colors inline-flex items-center gap-2 shadow-sm"
            >
              <Sparkles size={14} />
              {i18n.language?.startsWith("es") ? "Analyzer" : "Analyzer"}
            </Link>
            <Link
              to={`/projects/${projectId}/chat`}
              data-testid="open-chat-btn"
              className="bg-white text-[#8B5CF6] border border-[#8B5CF6]/40 hover:bg-[#8B5CF6]/5 rounded-xl px-4 py-2 font-medium text-sm transition-colors inline-flex items-center gap-2"
            >
              <MessageCircle size={14} />
              Chat
            </Link>
            <button
              data-testid="upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="bg-[#003366] text-white hover:bg-[#002244] rounded-xl px-4 py-2 font-medium text-sm transition-colors inline-flex items-center gap-2 disabled:opacity-60"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
              {estimation ? t("pd.new_estimate") : t("pd.upload")}
            </button>
          </div>
        {estimation?.demo_mode && (
          <div
            data-testid="demo-banner"
            className="mt-4 flex items-center gap-2 px-3 py-2 bg-[#F59E0B]/20 border border-[#F59E0B] rounded-xl"
          >
            <AlertTriangle size={14} className="text-amber-800" />
            <span className="text-xs font-mono uppercase tracking-wider text-amber-900">
              {t("pd.demo_mode")}
            </span>
            <span className="text-xs text-amber-900">· {t("pd.demo_note")}</span>
            <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-amber-900 bg-white/50 px-2 py-0.5 rounded-xl" data-testid="detection-mode-badge">
              YOLO: {estimation.detection_mode || "mock"}
            </span>
          </div>
        )}
        {estimation && !estimation.demo_mode && (
          <div
            data-testid="yolo-real-banner"
            className="mt-4 flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-300 rounded-xl"
          >
            <CheckCircle2 size={14} className="text-green-700" />
            <span className="text-xs font-mono uppercase tracking-wider text-green-900">
              YOLO: {estimation.detection_mode}
            </span>
            <span className="text-xs text-green-900">· real inference</span>
          </div>
        )}
      </header>

      {!estimation ? (
        <UploadEmpty onBrowse={() => fileRef.current?.click()} uploading={uploading} />
      ) : (
        <>
          {/* Stats strip — module-filtered */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Stat label={t("pd.detections")} value={moduleDetections.length} data-testid="stat-detections" />
            <Stat label={`${t("pd.routing")} (${t("pd.linear_ft")})`} value={estimation.routing?.estimated_linear_feet || 0} />
            <Stat label={t("pd.materials")} value={fmtUsd(moduleBomMaterials)} />
            <Stat label={t("pd.grand_total")} value={fmtUsd(estimation.totals?.grand_total)} accent />
          </div>

          {/* Tabs */}
          <div className="border-b border-slate-200 mb-4 flex items-center gap-1 flex-wrap">
            {[
              { key: "viewer", label: t("pd.tab_viewer") },
              { key: "bom", label: t("pd.tab_bom") },
              { key: "schedule", label: t("pd.tab_schedule") },
              { key: "compliance", label: t("pd.tab_compliance") },
              { key: "legend", label: t("pd.tab_legend") },
              { key: "kmz", label: t("pd.tab_kmz") },
              { key: "plankmz", label: t("pd.tab_plankmz") },
              { key: "ai", label: t("pd.tab_ai") },
              { key: "survey", label: t("pd.tab_survey") },
              { key: "route", label: t("pd.tab_route") },
            ].map((tabItem) => (
              <button
                key={tabItem.key}
                data-testid={`tab-${tabItem.key}`}
                onClick={() => setTab(tabItem.key)}
                className={`px-4 py-2 text-sm font-mono uppercase tracking-wider transition-colors border-b-2 -mb-px ${
                  tab === tabItem.key
                    ? "border-[#003366] text-[#003366]"
                    : "border-transparent text-slate-500 hover:text-slate-900"
                }`}
              >
                {tabItem.label}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2 pb-2 flex-wrap">
              <button
                data-testid="export-pdf-btn"
                onClick={() => download("pdf")}
                className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-white text-slate-900 border border-slate-300 hover:bg-slate-50 rounded-xl inline-flex items-center gap-1.5"
              >
                <Download size={12} /> {t("pd.export_pdf")}
              </button>
              <button
                data-testid="export-csv-btn"
                onClick={() => download("csv")}
                className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-white text-slate-900 border border-slate-300 hover:bg-slate-50 rounded-xl inline-flex items-center gap-1.5"
              >
                <Download size={12} /> {t("pd.export_csv")}
              </button>
              <button
                data-testid="export-json-btn"
                onClick={() => download("json")}
                className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-white text-slate-900 border border-slate-300 hover:bg-slate-50 rounded-xl inline-flex items-center gap-1.5"
              >
                <FileText size={12} /> {t("pd.export_json")}
              </button>
              <button
                data-testid="push-crm-btn"
                onClick={pushToCrm}
                disabled={pushing}
                className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-[#0A0A0A] text-white hover:bg-black rounded-xl inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                {pushing ? <Loader2 size={12} className="animate-spin" /> : <Share2 size={12} />} {t("pd.push_crm")}
              </button>
              {project?.crm_origin && project?.crm_client_id && (
                <button
                  data-testid="push-crm-results-btn"
                  onClick={pushResultsToCrm}
                  disabled={pushing}
                  title={`crm_client_id: ${project.crm_client_id}`}
                  className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-[#EA580C] text-white hover:bg-[#c2410c] rounded-xl inline-flex items-center gap-1.5 disabled:opacity-60"
                >
                  {pushing ? <Loader2 size={12} className="animate-spin" /> : <Share2 size={12} />}
                  Enviar BOM + Cronograma al CRM
                </button>
              )}
            </div>
          </div>

          {/* Tab content */}
          {tab === "viewer" && (
            <div className="grid lg:grid-cols-[1fr_320px] gap-4">
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                {pages.length > 1 && (
                  <div className="flex items-center gap-1 p-2 border-b border-slate-200 bg-slate-50 overflow-x-auto">
                    {pages.map((p, i) => (
                      <button
                        key={i}
                        data-testid={`page-tab-${i}`}
                        onClick={() => setActivePage(i)}
                        className={`px-3 py-1 text-xs font-mono uppercase tracking-wider rounded-xl ${
                          activePage === i ? "bg-[#003366] text-white" : "text-slate-600 hover:bg-slate-100"
                        }`}
                      >
                        Page {i + 1}
                      </button>
                    ))}
                  </div>
                )}
                <div
                  className="relative w-full bg-slate-100 overflow-hidden"
                  style={{ aspectRatio: currentPage ? `${currentPage.width} / ${currentPage.height}` : "4 / 3" }}
                  data-testid="blueprint-viewer"
                >
                  {currentPage && (
                    <img
                      src={currentPage.image_b64}
                      alt={`Page ${activePage + 1}`}
                      className="absolute inset-0 w-full h-full object-contain"
                    />
                  )}
                  {filteredDetections.map((d, idx) => (
                    <div
                      key={idx}
                      className="absolute border-2 transition-all hover:z-20 hover:scale-110"
                      style={{
                        left: `${d.x * 100}%`,
                        top: `${d.y * 100}%`,
                        width: `${d.w * 100}%`,
                        height: `${d.h * 100}%`,
                        borderColor: CLASS_COLORS[d.class] || "#003366",
                        backgroundColor: `${CLASS_COLORS[d.class] || "#003366"}18`,
                      }}
                      title={`${d.class} · ${(d.confidence * 100).toFixed(0)}%`}
                    />
                  ))}
                </div>
              </div>
              <aside className="space-y-3">
                <div className="bg-white border border-slate-200 rounded-xl p-3">
                  <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">
                    {t("pd.detections")} · {pageDetections.length}
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    <button
                      data-testid="filter-all"
                      onClick={() => setClassFilter("all")}
                      className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-xl border ${
                        classFilter === "all" ? "bg-[#003366] text-white border-[#003366]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      all
                    </button>
                    {Object.entries(classCounts).map(([cls, n]) => (
                      <button
                        key={cls}
                        data-testid={`filter-${cls}`}
                        onClick={() => setClassFilter(cls)}
                        className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-xl border ${
                          classFilter === cls ? "bg-[#003366] text-white border-[#003366]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                        }`}
                      >
                        {cls} · {n}
                      </button>
                    ))}
                  </div>
                  <ul className="max-h-80 overflow-auto divide-y divide-slate-100">
                    {filteredDetections.slice(0, 80).map((d, i) => (
                      <li key={i} className="py-1.5 flex items-center justify-between text-xs font-mono">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-xl" style={{ backgroundColor: CLASS_COLORS[d.class] || "#003366" }} />
                          {d.class}
                        </span>
                        <span className="text-slate-500">{(d.confidence * 100).toFixed(0)}%</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>
          )}

          {tab === "bom" && (
            <>
              {/* Phase 10: Subsystem-grouped view (collapsible cards) */}
              {estimation?.id && (
                <div className="mb-4">
                  <BomBySubsystem estimationId={estimation.id} />
                </div>
              )}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between p-3 border-b border-slate-200 bg-slate-50">
                <div className="text-xs font-mono uppercase tracking-wider text-slate-500">
                  <span className={module === "electrical" ? "text-amber-700" : module === "low_voltage" ? "text-[#003366]" : "text-slate-700"}>
                    {module === "all" ? "ALL" : module === "electrical" ? "ELECTRICAL" : "LOW VOLTAGE"}
                  </span>
                  {" · "}{moduleBom.length} items · {t("pd.materials")}: <span className="text-slate-900">{fmtUsd(moduleBomMaterials)}</span>
                </div>
                <button
                  data-testid="save-bom-btn"
                  onClick={saveBom}
                  disabled={!bomDirty || savingBom}
                  className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-xl inline-flex items-center gap-1.5 transition-colors ${
                    bomDirty ? "bg-[#003366] text-white hover:bg-[#002244]" : "bg-slate-100 text-slate-400 cursor-not-allowed"
                  }`}
                >
                  {savingBom ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  {t("bom.save")}
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="bom-table">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-xs font-mono uppercase tracking-wider text-slate-500">
                      <th className="text-left p-3">{t("bom.category")}</th>
                      <th className="text-left p-3">{t("bom.item")}</th>
                      <th className="text-right p-3 w-24">{t("bom.qty")}</th>
                      <th className="text-left p-3 w-20">{t("bom.unit")}</th>
                      <th className="text-right p-3 w-32">{t("bom.unit_cost")}</th>
                      <th className="text-right p-3 w-32">{t("bom.total")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bomLocal.map((row, idx) => {
                      if (module !== "all") {
                        const cat = (row.category || "").toLowerCase();
                        if (cat !== module && !(module === "low_voltage" && cat === "lv")) return null;
                      }
                      return (
                      <tr key={idx} data-testid={`bom-row-${idx}`} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-3 text-xs font-mono text-slate-600">{row.category}</td>
                        <td className="p-3 text-sm font-medium text-slate-900">{row.item}</td>
                        <td className="p-3 text-right">
                          <input
                            data-testid={`bom-qty-${idx}`}
                            type="number"
                            step="0.01"
                            value={row.quantity}
                            onChange={(e) => updateBomRow(idx, "quantity", e.target.value)}
                            className="w-20 text-right bg-transparent border border-transparent hover:border-slate-300 focus:border-[#003366] rounded-xl px-2 py-1 text-sm font-mono focus:outline-none"
                          />
                        </td>
                        <td className="p-3 text-xs font-mono text-slate-600">{row.unit}</td>
                        <td className="p-3 text-right">
                          <input
                            data-testid={`bom-cost-${idx}`}
                            type="number"
                            step="0.01"
                            value={row.unit_cost}
                            onChange={(e) => updateBomRow(idx, "unit_cost", e.target.value)}
                            className="w-24 text-right bg-transparent border border-transparent hover:border-slate-300 focus:border-[#003366] rounded-xl px-2 py-1 text-sm font-mono focus:outline-none"
                          />
                        </td>
                        <td className="p-3 text-right text-sm font-mono text-slate-900">{fmtUsd(row.total_cost)}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                    <tr>
                      <td colSpan={5} className="p-3 text-right text-xs font-mono uppercase tracking-wider text-slate-500">{t("pd.materials")}</td>
                      <td className="p-3 text-right font-mono text-slate-900">{fmtUsd(estimation.totals.materials)}</td>
                    </tr>
                    <tr>
                      <td colSpan={5} className="p-3 text-right text-xs font-mono uppercase tracking-wider text-slate-500">{t("pd.labor")}</td>
                      <td className="p-3 text-right font-mono text-slate-900">{fmtUsd(estimation.totals.labor)}</td>
                    </tr>
                    <tr className="bg-[#003366] text-white">
                      <td colSpan={5} className="p-3 text-right text-xs font-mono uppercase tracking-wider">{t("pd.grand_total")}</td>
                      <td className="p-3 text-right font-mono text-base">{fmtUsd(estimation.totals.grand_total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            </>
          )}

          {tab === "schedule" && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-mono uppercase tracking-wider text-slate-500">
                {module === "all" ? "ALL PHASES" : module === "electrical" ? "ELECTRICAL PHASES" : "LOW VOLTAGE PHASES"} · {moduleSchedule.length}
              </div>
              <table className="w-full" data-testid="schedule-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-xs font-mono uppercase tracking-wider text-slate-500">
                    <th className="text-left p-3">{t("sched.phase")}</th>
                    <th className="text-left p-3 w-36">{t("sched.duration")}</th>
                    <th className="text-left p-3">{t("sched.notes")}</th>
                  </tr>
                </thead>
                <tbody>
                  {moduleSchedule.map((ph, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="p-3 text-sm font-medium text-slate-900">{ph.phase}</td>
                      <td className="p-3 text-sm font-mono text-[#003366]">{ph.duration}</td>
                      <td className="p-3 text-sm text-slate-700">{ph.notes}</td>
                    </tr>
                  ))}
                  {moduleSchedule.length === 0 && (
                    <tr><td colSpan={3} className="p-6 text-center text-sm text-slate-500 font-mono">No phases for this module</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === "compliance" && (
            <div className="grid md:grid-cols-2 gap-4" data-testid="compliance-panel">
              {moduleCompliance.map((c, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-start gap-2 mb-2">
                    <CheckCircle2 size={16} className="text-[#003366] mt-0.5" />
                    <div className="text-xs font-mono uppercase tracking-wider text-[#003366]">{c.code}</div>
                  </div>
                  <p className="text-sm text-slate-700">{c.note}</p>
                </div>
              ))}
              {moduleCompliance.length === 0 && (
                <div className="col-span-2 bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-500 font-mono">
                  No compliance notes for this module
                </div>
              )}
            </div>
          )}

          {tab === "ai" && (
            <AiPanel
              estimation={estimation}
              onRun={runAi}
              running={aiRunning}
              lang={i18n.language?.startsWith("es") ? "es" : "en"}
            />
          )}

          {tab === "legend" && (
            <LegendPanel estimationId={estimation.id} pages={estimation.pages || []} />
          )}

          {tab === "kmz" && (
            <KmzPanel projectId={project.id} />
          )}

          {tab === "plankmz" && (
            <PlanKmzOverlay projectId={project.id} pages={estimation?.pages || []} />
          )}

          {tab === "survey" && (
            <SiteSurveyPanel
              surveys={surveys}
              uploading={surveyUploading}
              onUpload={(f) => onSurveyUpload(f)}
              inputRef={surveyRef}
            />
          )}

          {tab === "route" && (
            <RoutePanel estimationId={estimation.id} lang={i18n.language?.startsWith("es") ? "es" : "en"} />
          )}
        </>
      )}
    </div>
  );
}

// --- Subcomponents ---
function Stat({ label, value, accent }) {
  return (
    <div className={`bg-white border rounded-xl p-3 ${accent ? "border-[#003366]" : "border-slate-200"}`}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 text-xl font-mono font-semibold ${accent ? "text-[#003366]" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function UploadEmpty({ onBrowse, uploading }) {
  const { t } = useTranslation();
  return (
    <div
      onClick={onBrowse}
      data-testid="upload-empty-zone"
      className="bg-white border-2 border-dashed border-slate-300 rounded-xl p-16 text-center cursor-pointer hover:border-[#003366] transition-colors"
    >
      {uploading ? (
        <Loader2 size={36} className="animate-spin text-[#003366] mx-auto" />
      ) : (
        <UploadCloud size={36} className="text-slate-400 mx-auto" />
      )}
      <div className="mt-4 text-sm font-medium text-slate-900">{t("pd.upload")}</div>
      <div className="text-xs text-slate-500 mt-1 font-mono">{t("pd.upload_hint")}</div>
    </div>
  );
}

function AiPanel({ estimation, onRun, running, lang }) {
  const { t } = useTranslation();
  const ai = estimation.ai_analysis;

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 font-mono text-sm" data-testid="ai-panel">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">CLAUDE SONNET 4.5 · NEC / TIA / BICSI</div>
          <h3 className="text-lg font-semibold text-slate-900 mt-1">AI Analysis</h3>
        </div>
        <button
          data-testid="run-ai-btn"
          onClick={onRun}
          disabled={running}
          className="bg-[#0A0A0A] text-white hover:bg-black rounded-xl px-4 py-2 text-xs font-mono uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-60"
        >
          {running ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
          {running ? t("pd.ai_running") : ai ? "Re-run analysis" : t("pd.run_ai")}
        </button>
      </div>

      {!ai ? (
        <div className="text-center py-12 text-slate-500 text-xs">
          {t("ai.empty")}
        </div>
      ) : (
        <div className="space-y-6" data-testid="ai-result">
          <section>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{t("ai.summary")}</div>
            <p className="text-sm text-slate-900 leading-relaxed bg-white border border-slate-200 rounded-xl p-3 font-sans">
              {ai.executive_summary}
            </p>
          </section>

          {ai.compliance_notes?.length > 0 && (
            <section>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{t("ai.compliance")}</div>
              <div className="space-y-2">
                {ai.compliance_notes.map((c, i) => (
                  <div key={i} className={`bg-white border rounded-xl p-3 flex items-start gap-2 ${
                    c.severity === "critical" ? "border-red-300" :
                    c.severity === "warning" ? "border-[#F59E0B]" : "border-slate-200"
                  }`}>
                    {c.severity === "critical" ? <AlertTriangle size={14} className="text-red-600 mt-0.5" /> :
                     c.severity === "warning" ? <AlertTriangle size={14} className="text-amber-600 mt-0.5" /> :
                     <Info size={14} className="text-[#003366] mt-0.5" />}
                    <div className="text-xs">
                      <div className="font-mono uppercase tracking-wider text-slate-500">{c.code}</div>
                      <div className="text-slate-900 mt-0.5 font-sans text-sm">{c.note}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {ai.optimization_suggestions?.length > 0 && (
            <section>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{t("ai.opt")}</div>
              <ul className="space-y-1.5">
                {ai.optimization_suggestions.map((s, i) => (
                  <li key={i} className="bg-white border border-slate-200 rounded-xl p-3 text-sm font-sans text-slate-900 flex gap-2">
                    <Sparkles size={14} className="text-[#003366] shrink-0 mt-0.5" />
                    {s}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {ai.risk_flags?.length > 0 && (
            <section>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{t("ai.risks")}</div>
              <div className="space-y-2">
                {ai.risk_flags.map((r, i) => (
                  <div key={i} className="bg-white border border-slate-200 rounded-xl p-3 font-sans">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-xl border ${
                        r.impact === "high" ? "bg-red-50 text-red-800 border-red-200" :
                        r.impact === "medium" ? "bg-[#F59E0B]/20 text-amber-900 border-[#F59E0B]" :
                        "bg-slate-100 text-slate-700 border-slate-200"
                      }`}>{r.impact}</span>
                      <span className="text-sm font-medium text-slate-900">{r.risk}</span>
                    </div>
                    <div className="text-xs text-slate-600">Mitigation: {r.mitigation}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {ai.disclaimer && (
            <div className="text-[11px] text-slate-500 border-t border-slate-200 pt-3 font-sans italic">{ai.disclaimer}</div>
          )}
        </div>
      )}
    </div>
  );
}


function SiteSurveyPanel({ surveys, uploading, onUpload, inputRef }) {
  const fmt = new Intl.NumberFormat("en-US");
  const survey = surveys?.[0];
  const data = survey?.data;

  if (!survey) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-12 text-center" data-testid="site-survey-empty">
        <ClipboardList size={42} className="text-slate-400 mx-auto" />
        <h3 className="mt-4 text-base font-semibold text-slate-900">No site survey uploaded yet</h3>
        <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
          Upload a DOCX or PDF site survey. Claude Sonnet 4.5 will extract devices, routes,
          materials, measurements, labels (D1, R1-PP2-PTO22-D46) and observations.
        </p>
        <button
          data-testid="upload-survey-empty-btn"
          onClick={() => inputRef?.current?.click()}
          disabled={uploading}
          className="mt-6 bg-[#003366] text-white hover:bg-[#002244] rounded-xl px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
          Upload site survey
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="site-survey-panel">
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">
              CLAUDE SONNET 4.5 · MULTIMODAL EXTRACTION
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mt-1" data-testid="survey-site-name">
              {data?.site?.name || "Site Survey"}
            </h3>
            <div className="text-xs font-mono text-slate-600 mt-1">
              {data?.site?.address && <span><MapPin size={10} className="inline mr-1" />{data.site.address} · </span>}
              {survey.filename} · images: {data?._meta?.images_attached ?? 0}
            </div>
          </div>
          <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-xl bg-green-50 text-green-800 border border-green-200">
            extracted
          </span>
        </div>
        {data?.summary && (
          <p className="text-sm text-slate-900 mt-3 leading-relaxed bg-slate-50 border border-slate-200 rounded-xl p-3">
            {data.summary}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          ["Devices", data?.devices?.length || 0],
          ["Routes", data?.routes?.length || 0],
          ["Materials", data?.materials?.length || 0],
          ["Measurements", data?.measurements?.length || 0],
          ["Observations", data?.observations?.length || 0],
        ].map(([label, value]) => (
          <div key={label} className="bg-white border border-slate-200 rounded-xl p-3">
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
            <div className="mt-1 text-2xl font-mono font-semibold text-slate-900">{fmt.format(value)}</div>
          </div>
        ))}
      </div>

      {data?.devices?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-mono uppercase tracking-wider text-slate-500">
            Devices & Labels
          </div>
          <div className="max-h-80 overflow-auto">
            <table className="w-full" data-testid="survey-devices-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-mono uppercase tracking-wider text-slate-500 sticky top-0">
                <tr><th className="text-left p-2">Label</th><th className="text-left p-2">Type</th><th className="text-right p-2 w-16">Qty</th><th className="text-left p-2">Notes</th></tr>
              </thead>
              <tbody>
                {data.devices.map((d, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="p-2 text-sm font-mono font-medium text-slate-900">{d.label || "—"}</td>
                    <td className="p-2 text-xs font-mono text-slate-700">{d.type || "other"}</td>
                    <td className="p-2 text-right font-mono text-slate-900 text-sm">{d.qty ?? 1}</td>
                    <td className="p-2 text-xs text-slate-600">{d.notes || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {data?.routes?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-mono uppercase tracking-wider text-slate-500">Routes</div>
            <ul className="divide-y divide-slate-100">
              {data.routes.map((r, i) => (
                <li key={i} className="p-3 text-sm font-mono">
                  <div className="text-slate-900">{r.from || "?"} → {r.to || "?"}</div>
                  <div className="text-xs text-slate-600 mt-1">{r.cable_type || "cable"} · {r.length_ft ? `${r.length_ft} ft` : "len. n/a"}</div>
                  {r.notes && <div className="text-xs text-slate-500 mt-0.5">{r.notes}</div>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {data?.measurements?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-mono uppercase tracking-wider text-slate-500">Measurements</div>
            <ul className="divide-y divide-slate-100">
              {data.measurements.map((m, i) => (
                <li key={i} className="p-3 text-sm flex items-start gap-2 font-mono">
                  <Ruler size={12} className="text-[#003366] mt-1 shrink-0" />
                  <div>
                    <div className="text-slate-900">{m.description}</div>
                    <div className="text-xs text-[#003366]">{m.value != null ? `${m.value} ${m.unit || ""}` : m.unit || ""}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {data?.materials?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-mono uppercase tracking-wider text-slate-500">Materials extracted</div>
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-mono uppercase tracking-wider text-slate-500">
              <tr><th className="text-left p-2">Item</th><th className="text-right p-2 w-24">Qty</th><th className="text-left p-2 w-20">Unit</th><th className="text-left p-2">Notes</th></tr>
            </thead>
            <tbody>
              {data.materials.map((m, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="p-2 text-sm font-medium text-slate-900">{m.item}</td>
                  <td className="p-2 text-right font-mono text-sm">{m.qty}</td>
                  <td className="p-2 text-xs font-mono text-slate-600">{m.unit}</td>
                  <td className="p-2 text-xs text-slate-600">{m.notes || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {data?.observations?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3">Observations</div>
            <ul className="space-y-2 text-sm">
              {data.observations.map((o, i) => (
                <li key={i} className="flex gap-2">
                  <Info size={14} className="text-[#003366] shrink-0 mt-0.5" />
                  <span className="text-slate-800">{o}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {data?.open_questions?.length > 0 && (
          <div className="bg-white border border-[#F59E0B] rounded-xl p-4">
            <div className="text-xs font-mono uppercase tracking-wider text-amber-800 mb-3">Open questions</div>
            <ul className="space-y-2 text-sm">
              {data.open_questions.map((q, i) => (
                <li key={i} className="flex gap-2">
                  <AlertTriangle size={14} className="text-amber-700 shrink-0 mt-0.5" />
                  <span className="text-slate-800">{q}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function ModuleSwitcher({ module, onChange }) {
  const modules = [
    { key: "electrical",  label: "Electrical",  sub: "NEC 2026",     color: "#EAB308", bgIdle: "bg-white text-slate-700", bgActive: "bg-amber-400 text-amber-950 border-amber-400" },
    { key: "low_voltage", label: "Low Voltage", sub: "TIA · BICSI",  color: "#003366", bgIdle: "bg-white text-slate-700", bgActive: "bg-[#003366] text-white border-[#003366]" },
    { key: "all",         label: "All",         sub: "Both modules", color: "#64748B", bgIdle: "bg-white text-slate-700", bgActive: "bg-slate-900 text-white border-slate-900" },
  ];
  return (
    <div className="inline-flex bg-slate-100 rounded-xl p-1 border border-slate-200" data-testid="module-switcher">
      {modules.map((m) => (
        <button
          key={m.key}
          data-testid={`module-${m.key}-btn`}
          onClick={() => onChange(m.key)}
          className={`px-4 py-2 rounded-xl text-xs font-mono uppercase tracking-wider border transition-all ${
            module === m.key ? m.bgActive + " shadow-sm" : m.bgIdle + " border-transparent hover:bg-white"
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: m.color }} />
            <div className="text-left leading-tight">
              <div className="font-semibold">{m.label}</div>
              <div className={`text-[9px] opacity-70 ${module === m.key ? "text-current" : "text-slate-500"}`}>{m.sub}</div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}


function RoutePanel({ estimationId, lang }) {
  const [photos, setPhotos] = useState([]);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const es = lang === "es";

  useEffect(() => {
    api.get(`/estimations/${estimationId}/route-suggestions`).then(({ data }) => {
      if (data && data[0]?.result) setResult(data[0].result);
    }).catch(() => {});
  }, [estimationId]);

  const handleFiles = (files) => {
    const arr = Array.from(files || []).slice(0, 10);
    setPhotos((prev) => [...prev, ...arr].slice(0, 10));
  };

  const run = async () => {
    setRunning(true); setError(null);
    try {
      const fd = new FormData();
      photos.forEach((p) => fd.append("photos", p));
      fd.append("language", lang);
      const { data } = await api.post(`/estimations/${estimationId}/suggest-route`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Route suggestion failed");
    } finally { setRunning(false); }
  };

  return (
    <div className="space-y-4" data-testid="route-panel">
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">CLAUDE SONNET 4.5 · MULTIMODAL · NEC / TIA / BICSI</div>
            <h3 className="font-display text-lg font-bold text-slate-900 mt-1">
              {es ? "Sugerencia de Ruta con J-Hooks" : "Route Suggestion with J-Hooks"}
            </h3>
            <p className="text-xs text-slate-600 mt-1 max-w-2xl">
              {es
                ? "Sube fotos o frames del video del sitio. La IA usa el plano + las fotos para proponer la ruta óptima, colocar J-Hooks cada 4-5 ft y calcular mediciones por subsistema."
                : "Upload site photos or video frames. AI uses the plan + photos to propose the optimal route, place J-Hooks every 4-5 ft, and compute per-subsystem measurements."}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-xl text-sm font-medium cursor-pointer hover:bg-slate-50" data-testid="route-photos-label">
            <UploadCloud size={14} />
            {es ? "Añadir fotos" : "Add photos"}
            <input type="file" accept="image/*" multiple className="hidden" data-testid="route-photos-input"
              onChange={(e) => handleFiles(e.target.files)} />
          </label>
          <div className="text-xs font-mono text-slate-600">
            {photos.length} {es ? "foto(s) cargada(s) · max 10" : "photo(s) loaded · max 10"}
          </div>
          <button
            data-testid="route-run-btn"
            onClick={run}
            disabled={running}
            className="ml-auto bg-[#EA580C] text-white hover:bg-[#C2410C] rounded-xl px-4 py-2 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60"
          >
            {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {running ? (es ? "Analizando plano + fotos…" : "Analyzing plan + photos…") : (es ? "Sugerir ruta" : "Suggest route")}
          </button>
        </div>
        {error && <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 font-mono">{error}</div>}

        {photos.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {photos.map((p, i) => (
              <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
                <img src={URL.createObjectURL(p)} alt={p.name} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )}
      </div>

      {result && (
        <div className="space-y-4" data-testid="route-result">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">{es ? "Resumen" : "Summary"}</div>
            <p className="text-sm text-slate-900 leading-relaxed">{result.summary}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              [es?"Waypoints":"Waypoints", (result.waypoints||[]).length],
              ["J-Hooks", (result.j_hooks||[]).length],
              [es?"Mediciones":"Measurements", (result.subsystem_measurements||[]).length],
              ["BOM", (result.bom||[]).length],
              [es?"Fases":"Phases", (result.schedule||[]).length],
            ].map(([l, v]) => (
              <div key={l} className="bg-white border border-slate-200 rounded-xl p-3">
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{l}</div>
                <div className="mt-1 text-2xl font-mono font-semibold text-slate-900">{v}</div>
              </div>
            ))}
          </div>

          {(result.subsystem_measurements||[]).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-mono uppercase tracking-wider text-slate-500">{es?"Mediciones por subsistema":"Per-subsystem measurements"}</div>
              <table className="w-full">
                <thead className="bg-slate-50 text-[10px] font-mono uppercase tracking-wider text-slate-500">
                  <tr><th className="text-left p-2">{es?"Subsistema":"Subsystem"}</th><th className="text-right p-2">LF</th><th className="text-left p-2">{es?"Notas":"Notes"}</th></tr>
                </thead>
                <tbody>
                  {result.subsystem_measurements.map((m, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="p-2 text-sm font-mono">{m.subsystem}</td>
                      <td className="p-2 text-right font-mono text-[#003366] font-semibold">{m.length_ft}</td>
                      <td className="p-2 text-xs text-slate-600">{m.notes || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(result.j_hooks||[]).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-mono uppercase tracking-wider text-slate-500">
                J-Hooks placement ({result.j_hooks.length})
              </div>
              <div className="max-h-60 overflow-auto">
                <ul className="divide-y divide-slate-100">
                  {result.j_hooks.map((j, i) => (
                    <li key={i} className="p-2 text-xs font-mono flex items-center justify-between">
                      <span className="text-slate-900 font-semibold">{j.label}</span>
                      <span className="text-slate-600">x={j.x?.toFixed?.(3)} y={j.y?.toFixed?.(3)} · h={j.height_ft || "?"} ft</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {(result.bom||[]).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-mono uppercase tracking-wider text-slate-500">BOM {es?"sugerido":"suggested"}</div>
              <table className="w-full">
                <thead className="bg-slate-50 text-[10px] font-mono uppercase tracking-wider text-slate-500">
                  <tr><th className="text-left p-2">Item</th><th className="text-right p-2 w-20">Qty</th><th className="text-left p-2 w-20">Unit</th><th className="text-left p-2">Notes</th></tr>
                </thead>
                <tbody>
                  {result.bom.map((b, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="p-2 text-sm">{b.item}</td>
                      <td className="p-2 text-right font-mono">{b.qty}</td>
                      <td className="p-2 text-xs font-mono text-slate-600">{b.unit}</td>
                      <td className="p-2 text-xs text-slate-600">{b.notes || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(result.obstacles||[]).length > 0 && (
            <div className="bg-[#FFD600]/10 border border-[#F59E0B] rounded-xl p-4">
              <div className="text-xs font-mono uppercase tracking-wider text-amber-800 mb-2">{es?"Obstáculos":"Obstacles"}</div>
              <ul className="text-sm space-y-1">
                {result.obstacles.map((o, i) => <li key={i} className="flex gap-2"><AlertTriangle size={14} className="text-amber-700 shrink-0 mt-0.5" />{o}</li>)}
              </ul>
            </div>
          )}

          {result.disclaimer && (
            <div className="text-[11px] text-slate-500 italic border-t border-slate-200 pt-3">{result.disclaimer}</div>
          )}
        </div>
      )}
    </div>
  );
}

